/**
 * Werktage-Berechnung für Versand-Vorlaufzeiten („X Werktage vor dem
 * Auftragsdatum"). Spiegelt bewusst 1:1 `WorkingDays` im Backend
 * (hawk-rails/app/services/working_days.rb), damit die im Büro angezeigten
 * Vorschau-Termine exakt den vom Backend aufgelösten Terminen entsprechen:
 *
 *   - Sonntage zählen NICHT als Werktag und werden übersprungen (Samstag gilt
 *     als Werktag, damit die Verschiebung effektiv ~N Tage bleibt und nicht
 *     eine ganze Woche).
 *   - Fällt das ERGEBNIS auf einen Samstag, zieht `subtractWorkingDays` es auf
 *     Freitag vor und `addWorkingDays` schiebt es auf Montag (nach vorn statt
 *     zurück — ein Rückzug würde z.B. bei Abholterminen die Transitzeit
 *     unterschreiten).
 *   - Feiertage werden bewusst noch nicht berücksichtigt.
 *
 * Beide Funktionen sind seiteneffektfrei: das übergebene Datum bleibt
 * unverändert, die Uhrzeit wird mitgenommen.
 */

/**
 * Obergrenze der Schleife. Beide Funktionen zählen tageweise, und die Vorschau
 * im Versandzeitpunkt-Dialog rechnet bei jedem Tastendruck neu — ohne Deckel
 * friert die Oberfläche ein, sobald jemand eine absurd große Zahl eintippt.
 * 3650 Werktage sind gut zehn Jahre; darüber hinaus gibt es keinen sinnvollen
 * Versandtermin mehr.
 */
const MAX_WORKING_DAYS = 3650;

/** Bereinigt die Tagesangabe: nur ganze, positive und begrenzte Werte. */
function normalizeDays(days: number): number {
  if (!Number.isFinite(days) || days <= 0) return 0;
  return Math.min(Math.floor(days), MAX_WORKING_DAYS);
}

/** Datum, das `days` Werktage VOR `from` liegt (Pendant zu WorkingDays.before). */
export function subtractWorkingDays(from: Date, days: number): Date {
  const d = new Date(from.getTime());
  // „0 Werktage vorher" = der Tag selbst, ohne Wochenend-Verschiebung.
  let remaining = normalizeDays(days);
  if (remaining === 0) return d;

  while (remaining > 0) {
    d.setDate(d.getDate() - 1);
    if (d.getDay() !== 0) remaining -= 1; // 0 = Sonntag
  }
  if (d.getDay() === 6) d.setDate(d.getDate() - 1); // Samstag -> Freitag
  return d;
}

/** Datum, das `days` Werktage NACH `from` liegt (Pendant zu WorkingDays.after). */
export function addWorkingDays(from: Date, days: number): Date {
  const d = new Date(from.getTime());
  let remaining = normalizeDays(days);
  if (remaining === 0) return d;

  while (remaining > 0) {
    d.setDate(d.getDate() + 1);
    if (d.getDay() !== 0) remaining -= 1;
  }
  if (d.getDay() === 6) d.setDate(d.getDate() + 2); // Samstag -> Montag
  return d;
}
